import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import {
  BehaviorSubject,
  firstValueFrom,
  interval,
  Observable,
  Subscription,
  switchMap,
  take,
  timeout,
} from 'rxjs';
import { CyclopsHttp } from 'src/app/services/app.http';
import {
  EncryptionService,
  UserDetailsToken,
} from 'src/app/services/Encryption/encryption.service';
import { v4 as uuidv4 } from 'uuid';
import { IAumyeState } from 'src/app/state-management/states/aumye.state';
import { Store } from '@ngrx/store';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root',
})
export class LocationSosService {
  private locationSubscription?: Subscription;
  currentUser: UserDetailsToken | null = null;
  private orgId: string = '';
  private isInitialized = false;
  private locationAccessOverlay$ = new BehaviorSubject<{
    isVisible: boolean;
    type: string;
    userName: string;
  }>({
    isVisible: false,
    type: '',
    userName: '',
  });
  private serviceStatus$ = new BehaviorSubject<{
    isTracking: boolean;
    isInitialized: boolean;
    lastUpdate: number | null;
    error: string | null;
  }>({
    isTracking: false,
    isInitialized: false,
    lastUpdate: null,
    error: null,
  });
  private permissionDenied = false;
  private permissionGranted = false;
  constructor(
    private db: AngularFireDatabase,
    private encryptionService: EncryptionService,
    private http: CyclopsHttp,
    private store: Store<IAumyeState>
  ) {}

  getServiceStatus(): Observable<any> {
    return this.serviceStatus$.asObservable();
  }
  getLocationAccessOverlay(): Observable<any> {
    return this.locationAccessOverlay$.asObservable();
  }
  showLocationAccessOverlay(type: string, userName: string): void {
    this.locationAccessOverlay$.next({
      isVisible: true,
      type,
      userName,
    });
  }

  hideLocationAccessOverlay(): void {
    this.locationAccessOverlay$.next({
      isVisible: false,
      type: '',
      userName: '',
    });
  }
  async initializeUser(): Promise<boolean> {
    try {
      if (this.isInitialized && this.currentUser) {
        console.log('Location service already initialized');
        return true;
      }

      // Show loading message
      //   const loadingMessage = this.message.loading(
      //     'Initializing location services...',
      //     { nzDuration: 0 }
      //   );

      // Get current user details from encryption service
      const userDetails = await this.encryptionService.getCurrentUser();

      if (!userDetails) {
        // this.message.remove(loadingMessage.messageId);
        // this.message.error('Failed to get user details');
        this.updateServiceStatus({ error: 'Failed to get user details' });
        return false;
      }

      // Check if user has location access permission
      if (!userDetails.isLocationAccess) {
        // this.message.remove(loadingMessage.messageId);

        // Check user role
        const userRole = userDetails.role?.toLowerCase();
        const isSecurityRole = userRole === 'security';
        if (isSecurityRole) {
          // Show overlay for security role
          this.showLocationAccessOverlay('role_required', userDetails.fullName);
        } else {
          if (!this.hasShownAccessDenied) {
            this.showLocationAccessDeniedModal();
            this.hasShownAccessDenied = true;
          }
        }

        this.updateServiceStatus({ error: 'Location access denied for user' });
        return false;
      }

      this.currentUser = userDetails;
      this.orgId = userDetails.organizationID;

      //   this.message.remove(loadingMessage.messageId);

      // Check browser geolocation support
      if (!navigator.geolocation) {
        this.showGeolocationNotSupportedModal();
        this.updateServiceStatus({ error: 'Geolocation not supported' });
        return false;
      }

      // Request location permission with proper user consent
      const permissionGranted = await this.requestLocationPermission();

      if (!permissionGranted) {
        this.updateServiceStatus({ error: 'Location permission denied' });
        return false;
      }

      this.isInitialized = true;
      this.updateServiceStatus({
        isInitialized: true,
        error: null,
      });

      //   this.message.success(
      //     `Welcome ${userDetails.fullName}! Location services initialized.`
      //   );
      this.startLocationTracking();
      return true;
    } catch (error) {
      console.error('Error initializing user:', error);
      //   this.message.error('Failed to initialize location services');
      this.updateServiceStatus({ error: 'Initialization failed' });
      return false;
    }
  }
  private async requestLocationPermission(): Promise<boolean> {
    // If already granted, return true
    if (this.permissionGranted) {
      return true;
    }

    // If already denied, don't prompt again
    if (this.permissionDenied) {
      return false;
    }

    return new Promise((resolve) => {
      // Try to get current position first (this will trigger permission if needed)
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Permission granted
          this.permissionGranted = true;
          this.permissionDenied = false;
          resolve(true);
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            // Permission denied - remember this
            this.permissionDenied = true;
            this.permissionGranted = false;
            const userRole = this.currentUser?.role?.toLowerCase();
            const isSecurityRole = userRole === 'security';

            if (isSecurityRole) {
              // Show overlay for security role
              this.showLocationAccessOverlay(
                'permission_denied',
                this.currentUser?.fullName || 'User'
              );
            } else {
              // Show modal for other roles
              this.showLocationPermissionDeniedModal();
            }
          }
          resolve(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  }

  // Handle geolocation errors
  private handleGeolocationError(error: GeolocationPositionError): void {
    let errorMessage = 'Location access failed';

    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage =
          'Location access was denied. Please enable location in your browser settings.';

        // Show overlay if security role
        const userRole = this.currentUser?.role?.toLowerCase();
        if (userRole === 'security') {
          this.showLocationAccessOverlay(
            'permission_denied',
            this.currentUser?.fullName || 'User'
          );
        } else {
          this.showLocationPermissionDeniedModal();
        }
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage =
          'Location information is unavailable. Please check your GPS/network connection.';
        break;
      case error.TIMEOUT:
        errorMessage = 'Location request timed out. Please try again.';
        break;
    }

    // this.message.error(errorMessage);
  }
  private hasShownAccessDenied = false;
  private hasShownPermissionDenied = false;
  // Show access denied modal
  private showLocationAccessDeniedModal(): void {
    // this.modal.warning({
    //   nzTitle: 'Location Access Not Permitted',
    //   nzContent: `
    //     <p>Your account does not have permission to use location services.</p>
    //     <p>Please contact your administrator to enable location access for emergency services.</p>
    //   `,
    //   nzOkText: 'OK',
    // });
  }

  // Show geolocation not supported modal
  private showGeolocationNotSupportedModal(): void {
    // this.modal.error({
    //   nzTitle: 'Geolocation Not Supported',
    //   nzContent: `
    //     <p>Your browser doesn't support geolocation services.</p>
    //     <p>Please use a modern browser that supports location services.</p>
    //   `,
    //   nzOkText: 'OK',
    // });
  }

  // Show permission denied help modal
  private showLocationPermissionDeniedModal(): void {
    // this.modal.info({
    //   nzTitle: 'Location Permission Required',
    //   nzContent: `
    //     <div style="margin: 16px 0;">
    //       <p><strong>To enable location services:</strong></p>
    //       <ol style="margin: 8px 0; padding-left: 20px;">
    //         <li>Click the location icon in your browser's address bar</li>
    //         <li>Select "Allow" for location access</li>
    //         <li>Refresh the page and try again</li>
    //       </ol>
    //       <p style="margin-top: 12px;">
    //         <i>Location access is essential for emergency response features.</i>
    //       </p>
    //     </div>
    //   `,
    //   nzOkText: 'Got it',
    // });
  }

  // Start location tracking
  async startLocationTracking(updateIntervalMs: number = 10000): Promise<void> {
    if (!this.isInitialized || !this.currentUser) {
      throw new Error('Service not initialized. Call initializeUser() first.');
    }

    return new Promise((resolve, reject) => {
      // Get initial location
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.updateLocationInFirebase(
            position.coords.latitude,
            position.coords.longitude,
            position.coords.accuracy
          );

          // Set up periodic updates
          this.locationSubscription = interval(updateIntervalMs)
            .pipe(
              switchMap(
                () =>
                  new Promise<GeolocationPosition>((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, {
                      enableHighAccuracy: true,
                      timeout: 15000,
                      maximumAge: 0, // 1 minute cache
                    });
                  })
              )
            )
            .subscribe({
              next: (position) => {
                this.updateLocationInFirebase(
                  position.coords.latitude,
                  position.coords.longitude,
                  position.coords.accuracy
                );
              },
              error: (error) => {
                console.error('Error getting location:', error);
                this.updateServiceStatus({ error: 'Location tracking error' });
              },
            });

          this.updateServiceStatus({
            isTracking: true,
            error: null,
          });

          //   this.notification.success(
          //     'Location Tracking Started',
          //     `Your location is now being tracked for safety purposes.`,
          //     { nzDuration: 3000 }
          //   );

          resolve();
        },
        (error) => {
          this.handleGeolocationError(error);
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        }
      );
    });
  }

  // Update location in Firebase with organization structure
  private async updateLocationInFirebase(
    latitude: number,
    longitude: number,
    accuracy?: number
  ): Promise<void> {
    if (!this.currentUser || !this.orgId) {
      console.error('User or organization not initialized');
      return;
    }

    try {
      const path = `LocationSos/${this.orgId}/ActiveUsers/${this.currentUser.userID}`;

      // Get current record to preserve SOS data using firstValueFrom with timeout
      let currentRecord;
      try {
        currentRecord = await firstValueFrom(
          this.db.object(path).valueChanges().pipe(
            take(1),
            timeout(5000) // Add 5 second timeout to prevent hanging
          )
        );
      } catch (error) {
        console.warn(
          'Could not retrieve current record, continuing without it:',
          error
        );
        currentRecord = null;
      }

      // Get battery level safely with proper platform detection
      let batteryLevel: number | null = null;
      try {
        const platform = Capacitor.getPlatform();
        const isNative = Capacitor.isNativePlatform();

        // Skip battery check for iOS native app
        if (platform === 'ios' && isNative) {
          batteryLevel = null;
        }
        // Skip battery check for iOS web browsers (Safari, Chrome on iPhone/iPad)
        else if (
          platform === 'web' &&
          /iPhone|iPad|iPod/i.test(navigator.userAgent)
        ) {
          batteryLevel = null;
        }
        // Try to get battery for Android and other platforms
        else {
          batteryLevel = (await this.getBatteryLevel()) ?? null;
        }
      } catch (error) {
        console.warn('Could not get battery level:', error);
        batteryLevel = null;
      }

      // Get device info safely
      let deviceInfo = 'Unknown';
      try {
        deviceInfo = this.getDeviceInfo();
      } catch (error) {
        console.warn('Could not get device info:', error);
        deviceInfo = Capacitor.getPlatform();
      }

      // Create location update data
      const locationUpdateData: any = {
        userId: this.currentUser.userID,
        organizationId: this.orgId,
        name: this.currentUser.fullName,
        role: this.currentUser.role,
        department: this.currentUser.employeeDepartment,
        phoneNumber: this.currentUser.phoneNumber,
        email: this.currentUser.email,
        latitude,
        longitude,
        lastUpdated: Date.now(),
        isActive: true,
        profilePicture: this.currentUser.userID || null,
        accuracy: accuracy || null,
        battery: batteryLevel,
        deviceInfo: deviceInfo,
      };

      // Preserve SOS data if it exists
      if (currentRecord && (currentRecord as any).isSOS) {
        locationUpdateData.isSOS = (currentRecord as any).isSOS;
        locationUpdateData.sosTimestamp = (currentRecord as any).sosTimestamp;
      }

      // Use update instead of set to merge data
      await this.db.object(path).update(locationUpdateData);

      this.updateServiceStatus({
        lastUpdate: Date.now(),
        error: null,
      });
    } catch (error: any) {
      console.error('Error updating location:', error);

      // Provide more detailed error message
      const errorMessage = error?.message
        ? `Failed to update location: ${error.message}`
        : 'Failed to update location';

      this.updateServiceStatus({ error: errorMessage });
    }
  }

  // Get all active users for organization
  getActiveUsers(): Observable<LocationUser[]> {
    if (!this.orgId) {
      throw new Error('Organization not initialized');
    }

    return this.db
      .list<LocationUser>(`LocationSos/${this.orgId}/ActiveUsers`, (ref) =>
        ref.orderByChild('isActive').equalTo(true)
      )
      .valueChanges();
  }

  // Get recently active users
  getRecentActiveUsers(
    minutesThreshold: number = 10
  ): Observable<LocationUser[]> {
    if (!this.orgId) {
      throw new Error('Organization not initialized');
    }

    const threshold = Date.now() - minutesThreshold * 60 * 1000;
    return this.db
      .list<LocationUser>(`LocationSos/${this.orgId}/ActiveUsers`, (ref) =>
        ref.orderByChild('lastUpdated').startAt(threshold)
      )
      .valueChanges();
  }

  // Stop location tracking
  stopLocationTracking(): void {
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
      this.locationSubscription = undefined;
    }

    // Mark user as inactive
    if (this.currentUser && this.orgId) {
      const path = `LocationSos/${this.orgId}/ActiveUsers/${this.currentUser.userID}`;
      this.db.object(path).update({
        isActive: false,
        lastUpdated: Date.now(),
      });
    }

    this.updateServiceStatus({
      isTracking: false,
      error: null,
    });

    // this.notification.info(
    //   'Location Tracking Stopped',
    //   'Your location is no longer being tracked.',
    //   { nzDuration: 3000 }
    // );
  }
  async respondToSOS(requestId: string, responseData: any): Promise<void> {
    if (!this.isInitialized || !this.currentUser) {
      throw new Error('Service not initialized');
    }

    try {
      // Update the SOS request to mark as assigned
      const requestPath = `LocationSos/${this.orgId}/SOSAlerts/${requestId}`;
      await this.db.object(requestPath).update({
        isAssigned: true,
        assignedToUserId: this.currentUser.userID,
        assignedDateTime: new Date().toISOString(),
        status: 'assigned',
        lastUpdatedDateTime: new Date().toISOString(),
      });

      // Create response record in Firebase
      const responsePath = `LocationSos/${this.orgId}/SOSResponses/${responseData.respondId}`;
      await this.db.object(responsePath).set(responseData);

      // Update responder's location status
      const responderPath = `LocationSos/${this.orgId}/ActiveUsers/${this.currentUser.userID}`;
      await this.db.object(responderPath).update({
        isResponding: true,
        respondingToRequestId: requestId,
        lastUpdated: Date.now(),
      });

      console.log('SOS response saved to Firebase successfully');
    } catch (error) {
      console.error('Error saving SOS response to Firebase:', error);
      throw new Error('Failed to save response data');
    }
  }
  // Emergency SOS
  async sendSOS(requestId?: string): Promise<void> {
    if (!this.isInitialized || !this.currentUser) {
      throw new Error('Service not initialized');
    }

    const sosRequestId = requestId || uuidv4();

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const sosData = {
              requestId: sosRequestId,
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              lastUpdated: Date.now(),
              isActive: true,
              isSOS: true,
              sosTimestamp: Date.now(),
              accuracy: position.coords.accuracy,
            };

            const path = `LocationSos/${this.orgId}/ActiveUsers/${
              this.currentUser!.userID
            }`;
            await this.db.object(path).update(sosData);

            // Also create SOS alert record
            const sosAlertPath = `LocationSos/${this.orgId}/SOSAlerts/${sosRequestId}`;

            await this.db.object(sosAlertPath).set({
              ...sosData,
              userId: this.currentUser!.userID,
              userName: this.currentUser!.fullName,
              userPhone: this.currentUser!.phoneNumber,
              organizationId: this.orgId,
              alertId: `SOS_${Date.now()}_${this.currentUser!.userID}`,
            });

            await this.sendSOSToBackend(sosData);

            // this.notification.error(
            //   'SOS Alert Sent!',
            //   'Emergency alert has been sent to your organization.',
            //   { nzDuration: 0 }
            // );

            resolve();
          } catch (error) {
            reject(error);
          }
        },
        reject,
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        }
      );
    });
  }
  async sendResponseToBackend(responseData: any): Promise<void> {
    try {
      // Prepare API payload for backend
      const apiPayload = {
        requestId: responseData.requestId,
        respondId: responseData.respondId,
        respondUserId: responseData.respondUserId,
        respondUserName: responseData.respondUserName,
        requestLatitude: responseData.requestLatitude,
        requestLongitude: responseData.requestLongitude,
        responderLatitude: responseData.latitude,
        responderLongitude: responseData.requestLongitude,
      };

      const response = await firstValueFrom(
        this.http.post('RespondToSOSRequest', apiPayload)
      );

      console.log('SOS response sent to backend successfully:', response);
    } catch (error) {
      console.error('Failed to send SOS response to backend:', error);
    }
  }
  async completeSOSResponse(
    respondId: string,
    requestId: string
  ): Promise<void> {
    if (!this.currentUser || !this.orgId) {
      throw new Error('Service not initialized');
    }

    try {
      const now = new Date().toISOString();

      // Update response as completed
      const responsePath = `LocationSos/${this.orgId}/SOSResponses/${respondId}`;
      await this.db.object(responsePath).update({
        status: 'completed',
        completedDateTime: now,
        lastUpdatedDateTime: now,
      });

      // Update request as completed
      const requestPath = `LocationSos/${this.orgId}/SOSAlerts/${requestId}`;
      await this.db.object(requestPath).update({
        isCompleted: true,
        completedDateTime: now,
        status: 'completed',
        lastUpdatedDateTime: now,
      });

      // Update responder's status
      const responderPath = `LocationSos/${this.orgId}/ActiveUsers/${this.currentUser.userID}`;
      await this.db.object(responderPath).update({
        isResponding: false,
        respondingToRequestId: null,
        lastUpdated: Date.now(),
      });

      // Notify backend
      try {
        await firstValueFrom(
          this.http.post('CompleteSOSResponse', {
            respondId,
            requestId,
            completedDateTime: now,
          })
        );
      } catch (error) {
        console.error('Failed to notify backend of completion:', error);
      }

      //   this.notification.success(
      //     'Response Completed',
      //     'SOS response has been marked as completed successfully.',
      //     { nzDuration: 5000 }
      //   );
    } catch (error) {
      console.error('Error completing SOS response:', error);
      throw error;
    }
  }
  async cancelSOSResponse(
    respondId: string,
    requestId: string,
    reason?: string
  ): Promise<void> {
    if (!this.currentUser || !this.orgId) {
      throw new Error('Service not initialized');
    }

    try {
      // Update response as cancelled
      const responsePath = `LocationSos/${this.orgId}/SOSResponses/${respondId}`;
      await this.db.object(responsePath).update({
        status: 'cancelled',
        responseMessage: reason || 'Response cancelled',
        isActive: false,
        lastUpdatedDateTime: new Date().toISOString(),
      });

      // Update request to unassign it
      const requestPath = `LocationSos/${this.orgId}/SOSAlerts/${requestId}`;
      await this.db.object(requestPath).update({
        isAssigned: false,
        assignedToUserId: null,
        assignedDateTime: null,
        status: 'active',
        lastUpdatedDateTime: new Date().toISOString(),
      });

      // Update responder's status
      const responderPath = `LocationSos/${this.orgId}/ActiveUsers/${this.currentUser.userID}`;
      await this.db.object(responderPath).update({
        isResponding: false,
        respondingToRequestId: null,
        lastUpdated: Date.now(),
      });

      // Notify backend
      try {
        await firstValueFrom(
          this.http.post('CancelSOSResponse', {
            respondId,
            requestId,
            reason: reason || 'Response cancelled',
          })
        );
      } catch (error) {
        console.error('Failed to notify backend of cancellation:', error);
      }

      //   this.notification.info(
      //     'Response Cancelled',
      //     'Your response to the SOS request has been cancelled.',
      //     { nzDuration: 5000 }
      //   );
    } catch (error) {
      console.error('Error cancelling SOS response:', error);
      throw error;
    }
  }
  private async sendSOSToBackend(sosData: any): Promise<void> {
    try {
      // Prepare API payload
      const apiPayload = {
        requestId: sosData.requestId,
        latitude: sosData.latitude,
        longitude: sosData.longitude,
      };

      // Make API call to your backend
      const response = await firstValueFrom(
        this.http.post('CreateSOSRequest', apiPayload)
      );

      console.log('SOS sent to backend successfully:', response);
    } catch (error) {
      console.error('Failed to send SOS to backend:', error);
      // Don't throw error here - Firebase storage is the primary backup
      // Log the error but don't fail the entire SOS process
    }
  }
  // Add method to clear SOS status when resolved
  async clearSOSStatus(): Promise<void> {
    if (!this.currentUser || !this.orgId) {
      throw new Error('Service not initialized');
    }

    try {
      const path = `LocationSos/${this.orgId}/ActiveUsers/${this.currentUser.userID}`;

      await this.db.object(path).update({
        isSOS: false,
        sosTimestamp: null,
        lastUpdated: Date.now(),
      });

      //   this.notification.success(
      //     'SOS Status Cleared',
      //     'Emergency status has been resolved.',
      //     { nzDuration: 3000 }
      //   );
    } catch (error) {
      console.error('Error clearing SOS status:', error);
      throw error;
    }
  }

  // Check if user currently has active SOS
  async hasActiveSOS(): Promise<boolean> {
    if (!this.currentUser || !this.orgId) {
      return false;
    }

    try {
      const path = `LocationSos/${this.orgId}/ActiveUsers/${this.currentUser.userID}`;
      const record = await firstValueFrom(
        this.db.object(path).valueChanges().pipe(take(1))
      );

      return record && (record as any).isSOS === true;
    } catch (error) {
      console.error('Error checking SOS status:', error);
      return false;
    }
  }
  // Utility methods
  private async getBatteryLevel(): Promise<number | undefined> {
    try {
      // @ts-ignore - Battery API is experimental
      const battery = await navigator.getBattery?.();
      return battery ? Math.round(battery.level * 100) : undefined;
    } catch {
      return undefined;
    }
  }

  private getDeviceInfo(): string {
    try {
      const platform = Capacitor.getPlatform();
      const isNative = Capacitor.isNativePlatform();

      if (isNative) {
        // Native iOS or Android app
        return `${platform} (Native)`;
      } else {
        // Web browser
        const userAgent = navigator.userAgent || 'Unknown';

        // Extract browser info more safely
        let browserInfo = 'Unknown Browser';

        // Check for specific browsers
        if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
          browserInfo = 'Chrome';
        } else if (
          userAgent.includes('Safari') &&
          !userAgent.includes('Chrome')
        ) {
          browserInfo = 'Safari';
        } else if (userAgent.includes('Firefox')) {
          browserInfo = 'Firefox';
        } else if (userAgent.includes('Edg')) {
          browserInfo = 'Edge';
        }

        // Check for mobile
        const isMobile = /iPhone|iPad|iPod|Android/i.test(userAgent);
        const mobileType = isMobile ? ' Mobile' : ' Desktop';

        return `${platform} - ${browserInfo}${mobileType}`;
      }
    } catch (error) {
      console.error('Error getting device info:', error);
      return 'Unknown Device';
    }
  }

  private updateServiceStatus(update: Partial<any>): void {
    const current = this.serviceStatus$.value;
    this.serviceStatus$.next({ ...current, ...update });
  }

  // Calculate distance between two points
  getDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Earth's radius in km
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) *
        Math.cos(this.deg2rad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  onDispatchGetRequestsData() {
    // this.store.dispatch(new fromAdvancedSoSActions.GetEmergencySosData({}));
  }

  // Cleanup on service destroy
  ngOnDestroy(): void {
    this.stopLocationTracking();
  }
}
export interface LocationUser {
  userId: string;
  organizationId: string;
  name: string;
  role: string;
  department: string;
  phoneNumber: string;
  email: string;
  latitude: number;
  longitude: number;
  lastUpdated: number;
  isActive: boolean;
  profilePicture?: string;
  isSOS?: boolean;
  sosTimestamp?: number;
  accuracy?: number;
  battery?: number;
  deviceInfo?: string;
}
